import React from 'react';
import { TrendingUp, Package, Calendar, Plus } from 'lucide-react';
import { formatDateTime } from '../Helpers';

const RestockHistory = ({ restockHistory }) => {
    const totalRestocks = restockHistory.length;
    const totalQuantityAdded = restockHistory.reduce((sum, restock) => sum + restock.quantityAdded, 0);

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-4">
                        <TrendingUp className="h-12 w-12" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Restock History</h1>
                        <p className="text-teal-100 mt-1">Track all inventory replenishment activities</p>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center space-x-4">
                        <div className="bg-teal-100 rounded-lg p-3">
                            <TrendingUp className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Restocks</p>
                            <p className="text-2xl font-bold text-gray-900">{totalRestocks}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center space-x-4">
                        <div className="bg-green-100 rounded-lg p-3">
                            <Package className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Units Added</p>
                            <p className="text-2xl font-bold text-gray-900">{totalQuantityAdded.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Restock History Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-teal-600" />
                        Restock Activities
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    {restockHistory.length === 0 ? (
                        <div className="text-center py-12">
                            <TrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No restock activities</h3>
                            <p className="text-gray-500">Restock activities will appear here when you replenish inventory</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="p-4 font-medium text-gray-600">Date & Time</th>
                                    <th className="p-4 font-medium text-gray-600">Item</th>
                                    <th className="p-4 font-medium text-gray-600 text-right">Quantity Added</th>
                                    <th className="p-4 font-medium text-gray-600 text-right">New Stock Level</th>
                                </tr>
                            </thead>
                            <tbody>
                                {restockHistory.map(restock => (
                                    <tr key={restock.id} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="bg-teal-100 rounded-lg p-2">
                                                    <Calendar className="h-4 w-4 text-teal-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{formatDateTime(restock.restockDate)}</p>
                                                    <p className="text-sm text-gray-500">Activity #{restock.id.slice(-8)}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="bg-gray-100 rounded-lg p-2">
                                                    <Package className="h-4 w-4 text-gray-600" />
                                                </div>
                                                <p className="font-semibold text-gray-900">{restock.itemName}</p>
                                            </div>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="inline-flex items-center bg-green-50 rounded-lg px-3 py-2">
                                                <Plus className="h-4 w-4 text-green-600 mr-1" />
                                                <span className="font-bold text-green-600">{restock.quantityAdded}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="inline-flex items-center bg-blue-50 rounded-lg px-3 py-2">
                                                <Package className="h-4 w-4 text-blue-600 mr-1" />
                                                <span className="font-bold text-blue-600">{restock.newQuantity}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RestockHistory;